import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../types/common/types';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { CloudWorkloadSecurityAccountStatus, CloudWorkloadSecurityCompanyRecord } from '../types';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { showCreateAccountDialog } from '../../cloudWorkloadSecurityCreateAccountDialog/';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';

export class CloudWorkloadSecurityCustomerViewView extends BaseWebComponentView {
  dom: {
    $wrapper: HTMLElement;

    $noAccountWrapper: HTMLElement;
    $withAccountWrapper: HTMLElement;

    $createAccountText: GzTextWebComponent;
    $openConsoleText: GzTextWebComponent;

    $createAccountButton: GzButtonWebComponent;
    $openConsoleButton: GzButtonWebComponent;

    $createAccountTooltip: GzTooltipWebComponent;
    $expiredAccountTooltip: HTMLElement;
    $expiredAccountTooltipText: HTMLElement;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $noAccountWrapper: this.shadow.querySelector('#no-account'),
      $withAccountWrapper: this.shadow.querySelector('#with-account'),

      $createAccountText: this.shadow.querySelector('#create-text'),
      $openConsoleText: this.shadow.querySelector('#console-text'),

      $createAccountButton: this.shadow.querySelector('#create-button'),
      $openConsoleButton: this.shadow.querySelector('#console-button'),

      $createAccountTooltip: this.shadow.querySelector('#create-tooltip'),
      $expiredAccountTooltip: this.shadow.querySelector('#expired-tooltip'),
      $expiredAccountTooltipText: this.shadow.querySelector('#expired-tooltip-text'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getCloudWorkloadSecurity();
  }

  public setTranslatedStrings() {
    this.dom.$createAccountText.innerText = this.translations.createAccountText;
    this.dom.$createAccountButton.innerText = this.translations.createAccount;
    this.dom.$openConsoleText.innerText = this.translations.openConsoleText;
    this.dom.$openConsoleButton.innerText = this.translations.openConsole;
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(company: CloudWorkloadSecurityCompanyRecord) {
    toggleVisibility(this.dom.$noAccountWrapper, false);
    toggleVisibility(this.dom.$withAccountWrapper, false);
    toggleVisibility(this.dom.$expiredAccountTooltip, false);
    this.dom.$createAccountTooltip.tooltipText = '';
    this.dom.$openConsoleButton.disabled = false;
    this.dom.$createAccountButton.disabled = false;

    switch (company.accountStatus) {
      case CloudWorkloadSecurityAccountStatus.NOT_CREATED:
        toggleVisibility(this.dom.$noAccountWrapper, true);
        break;

      case CloudWorkloadSecurityAccountStatus.CREATED:
        toggleVisibility(this.dom.$withAccountWrapper, true);
        toggleVisibility(this.dom.$expiredAccountTooltip, false);
        break;

      case CloudWorkloadSecurityAccountStatus.EXPIRED:
        toggleVisibility(this.dom.$withAccountWrapper, true);
        toggleVisibility(this.dom.$expiredAccountTooltip, true);
        this.dom.$openConsoleButton.disabled = true;
        this.dom.$expiredAccountTooltipText.innerText = company.daysUntilExpire
          ? this.translations.licenseNotValidWithDaysLeft(company.daysUntilExpire)
          : this.translations.licenseNotValid;
        break;
    }

    this.removeLoadingState();
  }

  public showCreateAccountDialog(
    company: CloudWorkloadSecurityCompanyRecord | undefined,
    callback: () => Promise<void>
  ) {
    showCreateAccountDialog(this.translations, company?.companyName, callback);
  }

  public showCreateAccountSuccessToast() {
    showSuccessToast(this.translations.createAccountSuccessToast);
  }

  public showCreateAccountErrorToast(error?: Error) {
    showErrorToast(error ?? new Error(this.translations.createAccountErrorToast));
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }
}
